/**
 * BuilderWorkspacePage — chat-first parallel interface for a single project.
 *
 * Layout: full-area EndUserAppView (the real running app with sample data).
 * The conversational interface is provided by the persistent AskAIDock
 * mounted in AppLayout — it sits at the bottom of the viewport and is
 * the same dock used elsewhere in the app, so chat state survives
 * navigation and the user gets the familiar bottom-bar UX.
 *
 * No split-pane, no embedded agent UI — just preview + dock.
 */

import { useEffect } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useProjectStore } from '@/store/projectStore'
import { useAuth } from '@/auth/AuthContext'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import EndUserAppView from '@/pages/run/EndUserAppView'

export default function BuilderWorkspacePage() {
  const { project: projectParam } = useParams<{ project: string }>()
  const project = projectParam ? decodeURIComponent(projectParam) : ''
  const { user, loading: authLoading } = useAuth()
  const { currentProject, selectProject } = useProjectStore()
  useRunOnlyRedirect(project || undefined)

  // Keep the project store in sync — TopBar/Sidebar/AskAIDock all read currentProject.
  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  if (!authLoading && !user) {
    return <Navigate to="/" replace />
  }
  if (!project) {
    return <Navigate to="/builder" replace />
  }

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        background: 'var(--color-bg)',
        overflow: 'hidden',
      }}
    >
      <EndUserAppView project={project} variant="builder" />
    </div>
  )
}
